import React, { createContext, useState, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";

import { fetchLegacyUserInfo } from "@/shared/api/cPanel";
import {
  setTokens,
  deleteAllCookies,
  clearLocalStorage,
} from "@/shared/utils/systemUtils/systemUtils";
import { fetchLibraryLanguagesRequest, getSettingsRequest } from "@/shared/api/not-sorted";
import { libraryActions, settingsActions, systemActions } from "@/store/actions";
import useCookiesWithOptions from "@/shared/hooks/useCookiesWithOptions";
import { localStorageSafe } from "@/shared/utils/storage/storage";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { consoleLogError } from "@/shared/utils/console";
import { COOKIE_KEY_I_18_NEXT } from "@/shared/utils/multilang/constants";
import { SETTING } from "@/shared/utils/settings/constants";
import { storeSetting } from "@/shared/utils/settings/settings";
import { getStoredSettingsNextApp, validateSettingsNextApp } from "@/utils/settings/settings";
import useLogin from "@/shared/hooks/useLogin";
import { selectorUserInfo } from "@/shared/store/system/selectors";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [cookie, setCookie] = useCookiesWithOptions([COOKIE_KEY_I_18_NEXT]);

  const [isInit, setIsInit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [afterLoginCb, setAfterLoginCb] = useState(null);
  const user = useSelector(selectorUserInfo);
  const languageUI = useSelector(selectorSetting(SETTING.languageUI.id));

  const { openLoginWindow } = useLogin();

  // Fetches user info using cookies tokens.
  useEffect(() => {
    fetchLegacyUserInfo()
      .then((userInfo) => {
        if (userInfo) {
          dispatch(systemActions.setUserInfo(userInfo));
        }
      })
      .catch((e) => {
        consoleLogError("Auth", "Failed to fetch user info", e);
      })
      .finally(() => {
        setIsInit(true);
      });
  }, []);

  // Handles auth tokens when login.
  useEffect(() => {
    const handleAuthToken = async (message) => {
      if (message?.data?.error) {
        dispatch(systemActions.toast(message.error));
      } else if (message?.data?.access_token) {
        const { access_token: accessToken, refresh_token: refreshToken, expires_in } = message.data;
        if (accessToken) {
          setTokens(accessToken, refreshToken, expires_in);
          if (refreshToken) {
            const userInfo = await fetchLegacyUserInfo();

            if (userInfo) {
              dispatch(systemActions.setUserInfo(userInfo));

              if (afterLoginCb) {
                afterLoginCb(userInfo);
              }
            }
          }
        }
      }
    };

    window.addEventListener("message", handleAuthToken);

    return () => {
      window.removeEventListener("message", handleAuthToken);
    };
  }, []);

  // Handles auth state.
  useEffect(() => {
    if (user) {
      dispatch(systemActions.setIsLogin(true));
      handleAuthSuccess();
    } else {
      dispatch(systemActions.setIsLogin(false));
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (languageUI !== cookie[COOKIE_KEY_I_18_NEXT]) {
      setCookie(COOKIE_KEY_I_18_NEXT, languageUI);
    }
  }, [languageUI]);

  const login = async (cb) => {
    if (!user) {
      setLoading(true);

      try {
        openLoginWindow(() => {
          setLoading(false);
        });

        setAfterLoginCb(cb);
      } catch (e) {
        setLoading(false);
        setAfterLoginCb(null);
        dispatch(systemActions.toast(e.toString()));
      }
    }
  };

  const logout = () => {
    router.push("/");
    deleteAllCookies();
    dispatch(libraryActions.updateAllHistory([]));
    setTokens();
    if (localStorageSafe) {
      clearLocalStorage();
    }

    dispatch(systemActions.setUserInfo(null));
  };

  const handleAuthSuccess = async () => {
    const storedSettings = getStoredSettingsNextApp();
    const serverSettings = await getSettingsRequest();
    const libLangs = await fetchLibraryLanguagesRequest();

    // Validate local settings
    const settingsLocalInvalidFixed = {};
    validateSettingsNextApp(storedSettings, settingsLocalInvalidFixed);
    // Update invalid local settings in storage
    for (const key in settingsLocalInvalidFixed) {
      storeSetting(key, settingsLocalInvalidFixed[key]);
    }
    const settingsLocalFinal = {
      ...storedSettings,
      ...settingsLocalInvalidFixed,
    };
    // =======================

    // Validate server settings
    const settingsServerInvalidFixed = {};
    validateSettingsNextApp(storedSettings, settingsServerInvalidFixed);
    // Store all server settings in storage
    const serverSettingsFinal = {
      ...serverSettings,
      ...settingsServerInvalidFixed,
    };
    for (const key in serverSettingsFinal) {
      storeSetting(key, serverSettingsFinal[key]);
    }
    // =======================

    dispatch(
      settingsActions.updateSettingSuccess({
        ...settingsLocalFinal,
        ...serverSettingsFinal,
        [SETTING.libraryLanguages.id]: libLangs,
      }),
    );

    dispatch(
      libraryActions.fetchAllBooksData(undefined, undefined, () => {
        setLoading(false);
      }),
    );
  };

  return (
    <AuthContext.Provider
      value={{
        isLogin: !!user,
        isInit,
        user,
        loading,
        login,
        logout,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
